import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import { getPhoto, getFile } from '../../storageService';
import { getProviderProfile } from '../../services/providersApi';
import './FeaturedProvidersBanner.css';

// Datos dummy para servicios - movidos fuera del componente para evitar recreaciones
const dummyServicesProviders = [
    {
        username: 'juan_electricista',
        name: 'Juan Pérez',
        photo: null,
        subcategories: ['Instalaciones eléctricas', 'Reparaciones'],
    },
    {
        username: 'ana_plomera',
        name: 'Ana García',
        photo: null,
        subcategories: ['Plomería', 'Destapaciones'],
    },
    {
        username: 'carlos_pintor',
        name: 'Carlos López',
        photo: null,
        subcategories: ['Pintura interior', 'Pintura exterior'],
    },
    {
        username: 'diego_jardinero',
        name: 'Diego Martínez',
        photo: null,
        subcategories: ['Jardinería', 'Paisajismo', 'Poda'],
    }
];

// Datos dummy para productos - movidos fuera del componente para evitar recreaciones
const dummyProductsProviders = [
    {
        username: 'maria_reposteria',
        name: 'María González',
        photo: null,
        subcategories: ['Tortas', 'Cupcakes'],
    },
    {
        username: 'luis_panaderia',
        name: 'Luis Hernández',
        photo: null,
        subcategories: ['Pan artesanal', 'Facturas'],
    },
    {
        username: 'sofia_cosmetica',
        name: 'Sofía Rodríguez',
        photo: null,
        subcategories: ['Cosmética natural', 'Jabones'],
    },
    {
        username: 'pedro_artesanias',
        name: 'Pedro Fernández',
        photo: null,
        subcategories: ['Artesanías', 'Decoración', 'Madera'],
    }
];

const FeaturedProvidersBanner = ({ categoryType }) => {
    const navigate = useNavigate();
    const [featuredProviders, setFeaturedProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [displayEnabled, setDisplayEnabled] = useState(null); // null = no determinado, true = show, false = hide

    useEffect(() => {
        const fetchFeaturedProviders = async () => {
            try {
                // Obtener el JSON de configuración desde Firebase Storage
                const featuredProvidersConfig = await getFile('resources/featuredproviders.json');

                // Si display es false, no mostrar el banner
                if (!featuredProvidersConfig || !featuredProvidersConfig.display) {
                    setDisplayEnabled(false);
                    setFeaturedProviders([]);
                    return;
                }

                // Si display es true, mostrar loading y cargar datos
                setDisplayEnabled(true);
                setLoading(true);

                // Seleccionar los IDs de proveedores según el tipo de categoría
                const providerIds = categoryType === 'productos'
                    ? featuredProvidersConfig.productsproviders
                    : featuredProvidersConfig.serviceproviders;

                if (!providerIds || providerIds.length === 0) {
                    setFeaturedProviders([]);
                    setLoading(false);
                    return;
                }

                // Obtener información completa de cada proveedor desde el backend
                const providersData = await Promise.all(
                    providerIds.map(async (providerId) => {
                        try {
                            const providerData = await getProviderProfile(providerId);

                            if (!providerData) {
                                return null;
                            }

                            // Obtener la foto del proveedor desde Storage
                            const photoUrl = providerData.photo ? await getPhoto(providerData.photo) : null;

                            const processedProvider = {
                                username: providerData.username,
                                name: providerData.name,
                                photo: providerData.photo,
                                photoUrl: photoUrl,
                                subcategories: providerData.categories || [],
                                description: providerData.description || ''
                            };

                            return processedProvider;
                        } catch (error) {
                            console.error(`Error fetching provider ${providerId}:`, error);
                            return null;
                        }
                    })
                );

                // Filtrar proveedores válidos (no null)
                const validProviders = providersData.filter(provider => provider !== null);
                setFeaturedProviders(validProviders);

            } catch (error) {
                console.error('Error loading featured providers config:', error);

                // Si hay un error al cargar el JSON, usar datos dummy como fallback
                console.log('Error loading config, using dummy data as fallback');
                const dummyData = categoryType === 'productos'
                    ? dummyProductsProviders
                    : dummyServicesProviders;

                setFeaturedProviders(dummyData);
                setDisplayEnabled(true);
            } finally {
                setLoading(false);
            }
        };

        fetchFeaturedProviders();
    }, [categoryType]);

    const handleProviderClick = (username) => {
        navigate(`/${username}`);
    };

    // Si display no se ha determinado aún, no mostrar nada (no ocupar espacio)
    if (displayEnabled === null) {
        return null;
    }

    // Si display es false, no mostrar nada y no ocupar espacio
    if (displayEnabled === false) {
        return null;
    }

    // Si display es true pero está cargando, mostrar esqueleto
    if (displayEnabled === true && loading) {
        return (
            <div className={`featured-providers-banner featured-banner-skeleton ${categoryType === 'servicios' ? 'service-theme' : 'product-theme'}`}>
                <div className="banner-ad-icon skeleton-icon"></div>
                <div className="banner-title skeleton-title"></div>
                <div className="featured-providers-grid">
                    {Array.from({ length: 4 }, (_, index) => (
                        <div key={index} className="featured-provider-card skeleton-card">
                            <div className="featured-provider-header">
                                <div className="featured-provider-image skeleton-image"></div>
                                <div className="featured-provider-info">
                                    <div className="skeleton-name"></div>
                                    <div className="skeleton-categories">
                                        <div className="skeleton-chip"></div>
                                        <div className="skeleton-chip"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Si display es true pero no hay proveedores, no mostrar nada
    if (displayEnabled === true && featuredProviders.length === 0) {
        return null;
    }

    return (
        <div className={`featured-providers-banner ${categoryType === 'servicios' ? 'service-theme' : 'product-theme'}`}>
            <div className="banner-ad-icon">
                <Megaphone size={18} />
            </div>
            <h3 className="banner-title">¿Buscabas algo de esto?</h3>
            <div className={`featured-providers-grid ${featuredProviders.length === 3 ? 'three-providers' : ''}`}>
                {featuredProviders.slice(0, Math.min(featuredProviders.length, 4)).map((provider) => {
                    return (
                        <div
                            key={provider.username}
                            className="featured-provider-card"
                            onClick={() => handleProviderClick(provider.username)}
                        >
                            <div className="featured-provider-header">
                                <div className="featured-provider-image">
                                    <img
                                        src={provider.photoUrl || "/images/profile-preview-mobile.png"}
                                        alt={provider.name}
                                        onError={(e) => {
                                            e.target.src = "/images/profile-preview-mobile.png";
                                        }}
                                    />
                                </div>
                                <div className="featured-provider-info">
                                    <h4 className="featured-provider-name">
                                        {provider.name}
                                    </h4>
                                    <div className="featured-provider-categories">
                                        {provider.subcategories.slice(0, 2).map((category, index) => {
                                            return (
                                                <span key={index} className="featured-category-chip">
                                                    {category}
                                                </span>
                                            );
                                        })}
                                        {provider.subcategories.length > 2 && (
                                            <span className="featured-category-chip featured-more-chip">
                                                +{provider.subcategories.length - 2}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FeaturedProvidersBanner;
